import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import i18n from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Key for storing widget mode in Capacitor Preferences (more reliable on mobile)
const WIDGET_MODE_KEY = 'tanzeel_widget_mode';
const MENU_STYLE_KEY = 'tanzeel_menu_style';

// Map app language codes to default Quran translation IDs (verified from api.quran.com)
const languageToTranslation = {
  'en': { id: '20', name: 'Saheeh International' },       // ID 20 is Saheeh International
  'ar': { id: '169', name: 'Al-Muyassar' },               // Arabic tafsir
  'bn': { id: '161', name: 'Taisirul Quran' },            // Bengali
  'ur': { id: '234', name: 'Fatah Muhammad Jalandhari' }, // Urdu
  'id': { id: '33', name: 'Indonesian Islamic Affairs Ministry' }, // Indonesian
  'fr': { id: '31', name: 'Muhammad Hamidullah' },        // French
  'es': { id: '83', name: 'Sheikh Isa Garcia' },          // Spanish
  'zh': { id: '56', name: 'Ma Jian (Simplified)' },       // Chinese
  'tr': { id: '77', name: 'Diyanet Isleri' },             // Turkish
  'so': { id: '46', name: 'Mahmud Muhammad Abduh' },      // Somali
};

const getDefaultTranslation = (langCode) => {
  return languageToTranslation[langCode] || languageToTranslation['en'];
};

const defaultSettings = {
  textSize: 'medium',
  reciter: '10', // Sa'ud ash-Shuraim (FREE)
  reciterName: "Sa'ud ash-Shuraim",
  translation: '20', // Saheeh International (verified ID)
  translationName: 'Saheeh International',
  selectedTranslations: ['20'], // Array of selected translation IDs
  primaryColor: '#044D29',
  accentColor: '#D4AF37',
  lastReadSurah: 1,
  lastReadAyah: 1,
  lastReadPage: 1, // For book view
  repeatMode: 'none', // none, surah, ayah
  autoPlay: false,
  bookView: false, // Book view mode
  showBottomNav: true, // Show/hide bottom navigation menu
  showAudioPanel: true, // Show/hide expandable audio player panel (play/pause button always visible)
  showSurahHeader: true, // Show/hide the top banner on Surah page (keeps back btn & theme toggle)
  showProgressCard: true, // Show/hide the reading progress card on Quran page
  showFloatingMenu: true, // Show/hide the floating action button menu
  hideAllMenus: false, // NEW: Hide BOTH floating menu AND bottom nav completely
  audioPlayerEnabled: true, // Master on/off for audio player functionality
  autoSyncTranslation: true, // Auto-sync translation with app language
  // Quran Reading Settings (unified system)
  quranScript: 'madina', // Script style: 'madina' (Uthmani) or 'indopak'
  tajweedMode: 'off', // Tajweed+ mode: 'off', 'subtle', 'full'
  // Legacy (kept for backward compatibility, maps to quranScript)
  arabicScript: 'uthmani', // Arabic script style: 'uthmani', 'indopak', 'simple'
  floatingMenuStyle: 'hamburger', // 'hamburger', 'fab' (floating action button - Pro only), or 'bottom' (bottom nav - Pro only)
  madhab: 'hanafi', // Islamic school of thought: 'hanafi', 'shafii', 'maliki', 'hanbali'
  widgetMode: false, // Quick access mode - opens app directly to floating menu
  // Dua Sharing Preferences (Content Density for Visual Cards)
  duaSharingArabic: true,      // Include Arabic text in shared Dua cards
  duaSharingTranslation: true, // Include translation in shared Dua cards
  duaSharingTransliteration: false, // Include transliteration in shared Dua cards
};

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    let savedSettings = defaultSettings;
    try {
      const saved = localStorage.getItem('noor-settings');
      savedSettings = saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
    } catch (e) {
      console.error('[Settings] Corrupted saved settings, falling back to defaults:', e);
      try { localStorage.removeItem('noor-settings'); } catch (_) {}
    }
    
    // Always sync translation with current language on init
    const currentLang = i18n.language?.split('-')[0] || 'en';
    const defaultTrans = getDefaultTranslation(currentLang);
    
    return {
      ...savedSettings,
      translation: defaultTrans.id,
      translationName: defaultTrans.name,
      selectedTranslations: [defaultTrans.id],
      autoSyncTranslation: true,
    };
  });
  const [loading, setLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Load widget mode from Capacitor Preferences on native (more reliable than localStorage)
  useEffect(() => {
    const loadNativeSettings = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const [widgetModeResult, menuStyleResult] = await Promise.all([
            Preferences.get({ key: WIDGET_MODE_KEY }),
            Preferences.get({ key: MENU_STYLE_KEY }),
          ]);
          
          const widgetMode = widgetModeResult.value === 'true';
          const menuStyle = menuStyleResult.value || 'hamburger';
          
          console.log('[SettingsContext] Loaded from native prefs - widgetMode:', widgetMode, 'menuStyle:', menuStyle);
          
          // Update settings with native preferences (these take priority)
          setSettings(prev => ({
            ...prev,
            widgetMode,
            floatingMenuStyle: menuStyle,
          }));
        } catch (error) {
          console.error('[SettingsContext] Failed to load native settings:', error);
        }
      }
      setSettingsLoaded(true);
    };
    
    loadNativeSettings();
  }, []);

  // Sync with localStorage
  useEffect(() => {
    localStorage.setItem('noor-settings', JSON.stringify(settings));
  }, [settings]);
  
  // Sync widget mode to Capacitor Preferences on native
  useEffect(() => {
    if (!settingsLoaded) return; // Don't save until we've loaded native prefs
    
    const saveNativeSettings = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          await Promise.all([
            Preferences.set({ key: WIDGET_MODE_KEY, value: String(settings.widgetMode) }),
            Preferences.set({ key: MENU_STYLE_KEY, value: settings.floatingMenuStyle }),
          ]);
          console.log('[SettingsContext] Saved to native prefs - widgetMode:', settings.widgetMode, 'menuStyle:', settings.floatingMenuStyle);
        } catch (error) {
          console.error('[SettingsContext] Failed to save native settings:', error);
        }
      }
    };
    
    saveNativeSettings();
  }, [settings.widgetMode, settings.floatingMenuStyle, settingsLoaded]);

  // Apply global text size scaling
  useEffect(() => {
    const root = document.documentElement;
    // Desktop-friendly text sizes - much larger scales
    const textSizeMap = {
      'small': '0.9',
      'medium': '1.1',
      'big': '1.4',
      'extrabig': '1.8'
    };
    const scale = textSizeMap[settings.textSize] || '1';
    root.style.setProperty('--text-scale', scale);
    root.style.setProperty('--arabic-scale', `${parseFloat(scale) * 1.3}`);
  }, [settings.textSize]);

  // Apply Arabic script style to HTML element (uses quranScript, falls back to arabicScript for backward compat)
  useEffect(() => {
    const root = document.documentElement;
    // Map quranScript to data attribute
    const scriptMap = { 'madina': 'uthmani', 'indopak': 'indopak' };
    const script = scriptMap[settings.quranScript] || settings.arabicScript || 'uthmani';
    root.setAttribute('data-arabic-script', script);
  }, [settings.quranScript, settings.arabicScript]);
  
  // Apply Tajweed mode to HTML element for global CSS access
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-tajweed-mode', settings.tajweedMode || 'off');
  }, [settings.tajweedMode]);

  // Load settings from backend on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data) {
          // Get current language to determine correct translation
          const currentLang = i18n.language?.split('-')[0] || 'en';
          const defaultTrans = getDefaultTranslation(currentLang);
          
          setSettings(prev => ({
            ...prev,
            ...response.data,
            textSize: response.data.text_size || prev.textSize,
            // Always use the language-based translation (override backend)
            translation: defaultTrans.id,
            translationName: defaultTrans.name,
            selectedTranslations: [defaultTrans.id],
            autoSyncTranslation: true,
          }));
        }
      } catch (error) {
        console.log('Using local settings');
      }
    };
    loadSettings();
  }, []);

  // Sync translation with app language when language changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      console.log('Language changed to:', lng);
      const langCode = lng?.split('-')[0] || 'en';
      const defaultTrans = getDefaultTranslation(langCode);
      console.log('Setting translation to:', defaultTrans);
      setSettings(prev => {
        // Always sync if autoSyncTranslation is true or undefined (default behavior)
        if (prev.autoSyncTranslation === false) return prev;
        return {
          ...prev,
          translation: defaultTrans.id,
          translationName: defaultTrans.name,
          selectedTranslations: [defaultTrans.id],
        };
      });
    };

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const updateSettings = useCallback(async (newSettings) => {
    setLoading(true);
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      
      // Save to backend async
      axios.put(`${API}/settings`, {
        id: 'default_user',
        theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
        primary_color: updated.primaryColor,
        accent_color: updated.accentColor,
        text_size: updated.textSize,
        reciter: updated.reciter,
        translation: updated.translation,
        last_read_surah: updated.lastReadSurah,
        last_read_ayah: updated.lastReadAyah,
      }).catch(() => console.log('Settings saved locally'));
      
      return updated;
    });
    setLoading(false);
  }, []);

  const updateLastRead = useCallback((surah, ayah) => {
    setSettings(prev => ({
      ...prev,
      lastReadSurah: surah,
      lastReadAyah: ayah
    }));
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, updateLastRead, loading }}>
      {children}
    </SettingsContext.Provider>
  );
};
